/**
 * Permission groups — read model for the settings-permission-groups feature.
 *
 * A permission group is a per-org capability set. A membership's
 * `permission_role` references one of these groups; capability checks across
 * the app read from the group's `capability_keys`. Owner / Admin / Member are
 * system groups (seeded per-org, read-only in v1); orgs can define custom
 * groups. Turn 6.2 ships the read-only view; create / edit / delete land in 6.3.
 */

export type PermissionGroupKind = 'system' | 'custom';

export interface PermissionGroup {
  id: string;
  name: string;
  kind: PermissionGroupKind;
  /** `owner` / `admin` / `member` for system groups; null for custom. */
  systemKey: string | null;
  /** Optional free-text description (custom groups; ≤ 280 chars). */
  description: string | null;
  /** Capability keys this group grants — references `capabilities.key`. */
  capabilityKeys: string[];
  /** Active memberships assigned this group in the org. */
  memberCount: number;
}

/** A capability — the v1 vocabulary, seeded into the `capabilities` table. */
export interface Capability {
  key: string;
  /** Matrix section bucket. */
  resource: string;
  label: string;
  description: string | null;
  sortOrder: number;
}

/** The capability-matrix sections, in display order. The `resource` value on a
 *  capability row keys into this list. */
export const RESOURCE_SECTIONS: ReadonlyArray<{
  resource: string;
  title: string;
}> = [
  { resource: 'documents', title: 'Documents' },
  { resource: 'tasks', title: 'Tasks' },
  { resource: 'members', title: 'Members' },
  { resource: 'settings', title: 'Settings' },
  { resource: 'org', title: 'Org' },
  { resource: 'other', title: 'Other' }
];

/** Fixed canonical order for the System section of the group list. */
export const SYSTEM_GROUP_ORDER: Record<string, number> = {
  owner: 0,
  admin: 1,
  member: 2
};

/**
 * Splits groups into the System section (fixed canonical Owner/Admin/Member
 * order) and the Custom section (alphabetical by name, case-insensitive).
 */
export function sortGroups(groups: readonly PermissionGroup[]): {
  system: PermissionGroup[];
  custom: PermissionGroup[];
} {
  const system = groups
    .filter((g) => g.kind === 'system')
    .sort(
      (a, b) =>
        (SYSTEM_GROUP_ORDER[a.systemKey ?? ''] ?? 99) -
        (SYSTEM_GROUP_ORDER[b.systemKey ?? ''] ?? 99)
    );
  const custom = groups
    .filter((g) => g.kind === 'custom')
    .sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );
  return { system, custom };
}

/**
 * Buckets the capability vocabulary into the resource sections, preserving
 * `sort_order` within each section. Sections with no capabilities are dropped.
 */
export function groupCapabilitiesByResource(
  capabilities: readonly Capability[]
): Array<{ resource: string; title: string; capabilities: Capability[] }> {
  return RESOURCE_SECTIONS.map((section) => ({
    ...section,
    capabilities: capabilities
      .filter((c) => c.resource === section.resource)
      .sort((a, b) => a.sortOrder - b.sortOrder)
  })).filter((section) => section.capabilities.length > 0);
}

/**
 * The capability set the editor renders as checked for a group. The Owner
 * system group is hardcoded to ALL capabilities regardless of what its stored
 * `capability_keys` say — Owner always has everything (the schema prevents its
 * row from being meaningfully mutated).
 */
export function resolveCapabilityKeys(
  group: PermissionGroup,
  allCapabilities: readonly Capability[]
): Set<string> {
  if (group.kind === 'system' && group.systemKey === 'owner') {
    return new Set(allCapabilities.map((c) => c.key));
  }
  return new Set(group.capabilityKeys);
}
